import { type EmbeddedLesson, type KeyTermForReview, type StreamChapter } from './StreamEditor.types';

function capitalize(text: string) {
    return text.substring(0, 1).toUpperCase() + text.substring(1);
}

/*
    Every key term in the course, for the review dialog.

    A lesson marked coming soon is left out, as Stream#getKeyTerms did: its terms are not live yet,
    so an editor reviewing the course should not see them.
*/
export function keyTermsForReview({
    chapters,
    lessonsById,
    stripFormatting,
}: {
    chapters: StreamChapter[];
    lessonsById: Record<string, EmbeddedLesson>;
    stripFormatting: (text: string) => string;
}): KeyTermForReview[] {
    return (chapters || []).flatMap((chapter, chapterIndex) =>
        (chapter.lesson_hashes || []).flatMap((lessonHash, lessonIndex) => {
            if (lessonHash.coming_soon) return [];

            const lesson = lessonsById[lessonHash.lesson_id];
            if (!lesson) return [];

            return (lesson.key_terms || []).map(rawKeyTerm => {
                const definition = (lesson.key_term_definitions || []).find(
                    candidate => candidate.key_term === rawKeyTerm,
                );

                return {
                    keyTerm: capitalize(stripFormatting(rawKeyTerm)),
                    rawKeyTerm,
                    definition: definition?.definition,
                    lessonId: lesson.id,
                    lessonTitle: lesson.title,
                    chapterIndex,
                    lessonIndex,
                };
            });
        }),
    );
}

// The terms that appear more than once, compared without case. Two lessons defining the same term
// is what an editor opens this dialog to find.
export function duplicateKeyTerms(terms: KeyTermForReview[]): string[] {
    const counts = new Map<string, number>();

    terms.forEach(term => {
        const key = term.keyTerm.toLowerCase();
        counts.set(key, (counts.get(key) || 0) + 1);
    });

    return [...counts.entries()].filter(([, count]) => count > 1).map(([key]) => key);
}

export function sortKeyTerms(terms: KeyTermForReview[]): KeyTermForReview[] {
    return [...terms].sort((a, b) => a.keyTerm.localeCompare(b.keyTerm));
}
